/**
 * Admin RBAC types (FROZEN backend contract R1–R9).
 * Pure types only — no runtime logic (except `isBackendPending` type-guard
 * and the `PERM_TTL_MS` constant).
 */

/** Cache TTL for permissions (`me.cacheTtl` / R5 `ttl`, seconds→ms). */
export const PERM_TTL_MS = 60_000;

export type AdminStatus = 'active' | 'disabled';

export interface AdminRoleRef {
  id: string;
  name: string;
  isSystem: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  status: AdminStatus;
  role: AdminRoleRef;
}

/** `GET /api/admin/staff/me/permissions` response. */
export interface MeResponse {
  admin: AdminUser;
  permissions: string[];
  cacheTtl: number;
}

/** Legacy catalog entry (no standalone catalog endpoint on backend — perms come from me/permissions). */
export interface PermissionEntry {
  key: string;
  group: string;
  description: string;
}

/** Legacy catalog response (unused — kept for type compat). */
export interface PermissionsResponse {
  permissions: PermissionEntry[];
  ttl: number;
}

/**
 * Staff row — backend `GET /api/admin/staff/users` returns
 * `{user_id, email, phone_number, first_name, last_name, role, status,
 * staff_disabled, perm_version, staff_roles: string[]}` (bare array).
 * The service maps it to this display shape.
 */
export interface StaffRow {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  /** Legacy `users.role` column (e.g. 'admin'); prefer `roleName`. */
  role: string;
  roleName: string;
  status: AdminStatus;
  staffDisabled: boolean;
  permVersion: number;
  staffRoles: string[];
}

/** `POST /api/admin/staff/roles` payload (name + display_name required). */
export interface CreateStaffDto {
  name: string;
  display_name: string;
  description?: string | null;
  permissionIds?: number[];
}

/** `PATCH /api/admin/staff/roles/:id` payload (no name change). */
export interface UpdateStaffDto {
  display_name?: string;
  description?: string | null;
  permissionIds?: number[];
}

/**
 * Role row — backend returns `{id: number, name, display_name,
 * description, is_system, permissions: string[]}` (bare array).
 * No permission-ID registry endpoint exists, so create/update with
 * `permissionIds` stays backend-managed (this page is view-only).
 */
export interface RoleRow {
  id: string;
  name: string;
  displayName?: string;
  description?: string | null;
  isSystem: boolean;
  permissions: string[];
}

/**
 * Audit-log row. Backend rows carry `{id, actor_user_id, actor_roles,
 * action, resource_type, resource_id, metadata (object), ip_address,
 * user_agent, created_at}` — the service maps them to this display shape.
 */
export interface AuditRow {
  id: string;
  actorEmail: string;
  actorRoles: string[];
  action: string;
  entity: string;
  entityId: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

/** Payout row for maker-checker (`requestedBy` ≠ approver). */
export interface PayoutRow {
  id: string;
  vendorId: string;
  vendorName: string;
  amount: number;
  status: string;
  requestedBy: string;
  createdAt: string;
}

/** Graceful-fallback marker when backend route is missing (404/501). */
export interface BackendPending {
  pending: true;
}

export function isBackendPending(x: unknown): x is BackendPending {
  return (
    typeof x === 'object' &&
    x !== null &&
    (x as BackendPending).pending === true
  );
}

/** 403 helper: authenticated but forbidden (or disabled account). */
export interface Api403 {
  status: 403;
  disabled?: boolean;
}
